import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import _ from "lodash";
import Modal from "react-modal";
//@ts-ignore
import { saveAs } from "file-saver";
import {
	Breadcrumbs,
	Header,
	LoadingState,
	Workspace,
	View,
	DefinitionList,
	DefinitionItem,
	Date as DateComponent,
	ModalHeader,
	Button,
} from "components/Shared";
import { Office } from "components/Viewers";
import ActivityTab from "components/Activities/ActivityDynamic";
import { translate, LocalNotification } from "lib";
import { UserContext } from "context";
import {
	getForm,
	putForm,
	recordFormView,
	getFormReplaceUploadUrl,
	replaceFormDocument,
	uploadFormFileToS3,
} from "actions/forms";
import { postEvent } from "actions/history";
import DeleteModal from "./DeleteModal";
import ReplaceModal from "./ReplaceModal";

// ----------------------------------------------------------------------------
// Form preview page — mirrors components/Documents/Show.
//
// Header carries Download / Edit / Trash buttons (admins / owner only); the
// Workspace renders three tabs:
//   - Preview: Google Docs embedded viewer for the .docx (same trick the
//     Documents Preview uses for officedocument mimetypes)
//   - Insights: form metadata (name, code, category, placeholders, etc.)
//   - Activity: shared ActivityDynamic feed filtered by form_id
//
// Breadcrumb structure matches Documents: Home / Properties / <Property> /
// Forms / <form name>.
// ----------------------------------------------------------------------------

const Insights = ({ form, name }: { form: any; name?: string }) => {
	const filesize = useMemo(() => {
		const bytes = Number(form?.filesize_bytes || 0);
		if (!bytes) return null;
		const kb = bytes / 1024;
		if (kb < 1024) return `${kb.toFixed(1)} KB`;
		return `${(kb / 1024).toFixed(2)} MB`;
	}, [form]);
	const creator =
		_.get(form, "created_by.name") ||
		[_.get(form, "created_by.firstname"), _.get(form, "created_by.surname")].filter(Boolean).join(" ") ||
		_.get(form, "created_by.email");
	return (
		<View>
			<DefinitionList>
				<DefinitionItem title="code" value={form.code} />
				<DefinitionItem title="name" value={form.name} />
				<DefinitionItem title="category" value={form.category} />
				<DefinitionItem title="description" value={form.description} />
				<DefinitionItem title="mimetype" value={form.mimetype} />
				{!!filesize && <DefinitionItem title="filesize" value={filesize} />}
				{!!creator && <DefinitionItem title="created_by" value={creator} />}
				{!!form.created_at && (
					<DefinitionItem title="created_at" value={<DateComponent date={form.created_at} />} />
				)}
				{!!form.updated_at && (
					<DefinitionItem title="updated_at" value={<DateComponent date={form.updated_at} />} />
				)}
			</DefinitionList>
		</View>
	);
};
// react-router routes named children by `name`; declare it via a side-channel.
(Insights as any).defaultProps = { name: "dashboard" };

// Inline edit modal — name / code / category / description. Mirrors the spirit
// of components/Documents/EditModal but kept light because forms only carry a
// small set of editable fields.
const EditModal = ({
	form,
	isOpen,
	onRequestClose,
	onSaved,
}: {
	form: any;
	isOpen: boolean;
	onRequestClose: () => void;
	onSaved: (updated: any) => void;
}) => {
	const dispatch = useDispatch();
	const [draft, setDraft] = useState({
		name: form?.name || "",
		code: form?.code || "",
		category: form?.category || "",
		description: form?.description || "",
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen) {
			setDraft({
				name: form?.name || "",
				code: form?.code || "",
				category: form?.category || "",
				description: form?.description || "",
			});
			setError(null);
		}
	}, [isOpen, form]);

	const onSave = async () => {
		setSaving(true);
		setError(null);
		try {
			const updated: any = await dispatch(putForm({ id: form.id, ...draft }) as any);
			onSaved(updated || { ...form, ...draft });
			onRequestClose();
		} catch (e) {
			setError((e as any)?.message || "Save failed");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={onRequestClose} contentLabel="edit-form" className="modal-content">
			<ModalHeader title="edit" onRequestClose={onRequestClose} />
			<section className="p-3">
				<div className="form-group">
					<label htmlFor="form-edit-name">{translate("name")}</label>
					<input
						id="form-edit-name"
						className="form-control"
						value={draft.name}
						onChange={(e) => {
							const value = e.target.value;
							setDraft((d) => ({ ...d, name: value }));
						}}
					/>
				</div>
				<div className="form-group">
					<label htmlFor="form-edit-code">{translate("code")}</label>
					<input
						id="form-edit-code"
						className="form-control"
						value={draft.code}
						onChange={(e) => {
							const value = e.target.value;
							setDraft((d) => ({ ...d, code: value }));
						}}
					/>
				</div>
				<div className="form-group">
					<label htmlFor="form-edit-category">{translate("category")}</label>
					<select
						id="form-edit-category"
						className="form-control"
						value={draft.category || ""}
						onChange={(e) => {
							const value = e.target.value;
							setDraft((d) => ({ ...d, category: value }));
						}}
					>
						{["", "Form", "Letter", "Agreement", "Notice", "Other"].map((opt) => (
							<option key={opt || "_blank"} value={opt}>
								{opt || "—"}
							</option>
						))}
					</select>
				</div>
				<div className="form-group">
					<label htmlFor="form-edit-description">{translate("description")}</label>
					<textarea
						id="form-edit-description"
						className="form-control"
						rows={3}
						value={draft.description}
						onChange={(e) => {
							const value = e.target.value;
							setDraft((d) => ({ ...d, description: value }));
						}}
					/>
				</div>
				{error && <div className="alert alert-danger py-2">{error}</div>}
			</section>
			<footer className="p-3 text-right">
				<Button onClick={onSave} disabled={saving}>
					{saving ? translate("saving") : translate("save")}
				</Button>
			</footer>
		</Modal>
	);
};

const FormShow = ({ match, history }: RouteComponentProps<{ id: string }>) => {
	const dispatch = useDispatch();
	const id = useMemo(() => _.get(match, "params.id"), [match]);
	const { currentUser } = useContext(UserContext) as { currentUser: any };
	const form = useSelector((state: any) => (id ? _.find(state.forms || [], { id }) : null)) as any;
	const [loading, setLoading] = useState(true);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [replaceOpen, setReplaceOpen] = useState(false);
	const [replacing, setReplacing] = useState(false);
	const replaceInputRef = useRef<HTMLInputElement>(null);
	const viewRecordedRef = useRef<string | null>(null);

	const fetchForm = useCallback(() => {
		if (!id) return;
		setLoading(true);
		const result: any = dispatch(getForm(id) as any);
		if (result && typeof result.finally === "function") {
			result.finally(() => setLoading(false));
		} else {
			setLoading(false);
		}
	}, [dispatch, id]);

	useEffect(() => fetchForm(), [fetchForm]);

	useEffect(() => {
		if (form?.id && viewRecordedRef.current !== form.id) {
			viewRecordedRef.current = form.id;
			dispatch(recordFormView(form.id) as any);
		}
	}, [form, dispatch]);

	const downloadUrl: string | undefined = form?.download_url;
	const downloadable = !!downloadUrl;
	const isOwner = !!form?.created_by?.id && form.created_by.id === currentUser?.id;
	const isAdmin = !!currentUser?.is_administrator;
	const canEdit = isOwner || isAdmin;
	// Templates only. A snapshot is the per-condition copy taken when the form
	// was bound; replacing one from here would edit a single condition's
	// document behind its back, and the API refuses it anyway.
	const canReplace = canEdit && !form?.parent_form_id;

	// Swaps the DOCUMENT only — name, code, category and description are left
	// alone, and the server re-extracts the placeholder and checkbox lists from
	// the new file.
	//
	// The upload goes to a FRESH S3 key (see the replace-upload-url endpoint):
	// a snapshot shares its template's key rather than a copy of the bytes, so
	// writing over the old object would rewrite the document under every
	// condition that ever used this form, signed ones included.
	const onReplaceFile = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files && event.target.files[0];
			// Clear the input so picking the SAME file again still fires onChange.
			event.target.value = "";
			if (!file || !form?.id) return;

			setReplacing(true);
			try {
				const presigned = await (dispatch(getFormReplaceUploadUrl(form.id) as any) as any);
				if (!presigned) throw new Error("Missing presigned_post_url");
				const key = await uploadFormFileToS3(presigned, file);
				await dispatch(
					replaceFormDocument({
						id: form.id,
						file_uri: key,
						mimetype: file.type || undefined,
						filesize_bytes: file.size,
					}) as any
				);
				fetchForm();
				setReplaceOpen(false);
				// Until now nothing told the person the upload had finished — the modal
				// just closed. A success toast is the receipt; it auto-dismisses, while
				// a failure toast stays up to be read (see lib/Notification).
				new LocalNotification("Form document replaced");
			} catch (e) {
				const message = (e as any)?.message || "Could not replace the document. Please try again.";
				new LocalNotification(message, message, "error");
			} finally {
				setReplacing(false);
			}
		},
		[dispatch, form, fetchForm]
	);

	const onDownload = useCallback(() => {
		if (!downloadUrl) return;
		fetch(downloadUrl)
			.then((r) => r.blob())
			.then((blob) => {
				const ext = ".docx";
				const base = (form?.name || form?.code || "form").toString();
				const filename = base.endsWith(ext) ? base : `${base}${ext}`;
				saveAs(blob, filename);
				// Mirror components/Documents/Show#downloadDocument — emit a
				// "Read" activity so the download appears in the Activity feed.
				// JSON deep-clone matches the Document Show pattern and works
				// around redux-memoize's 1s reference-based dedupe in memoAC.
				if (form?.link) {
					dispatch(postEvent("Read", JSON.parse(JSON.stringify(form))) as any);
				}
			})
			.catch(() => window.open(downloadUrl, "_blank"));
	}, [downloadUrl, form, dispatch]);

	if (loading && !form) return <LoadingState />;
	if (!form) return null;

	// F1: snapshot detection — parent_form_id is non-null when this form is a
	// per-condition snapshot cloned from a property-level template.
	// Consumes: api-contract.md §2.4 (serializer exposes parent_form_id).
	const isSnapshot = !!form.parent_form_id;
	const snapshotOfCode = form.parent_form_code || form.parent_form_id;

	// Prefer the flat property_id/property_name attributes the serializer now
	// exposes; fall back to the nested association if it's sideloaded.
	const propertyId = form.property_id || _.get(form, "property.id");
	const propertyName = form.property_name || _.get(form, "property.name");
	// `Breadcrumbs` translates each title once internally, so we pass raw
	// labels here. "forms" has no entry in the i18n dictionary which is why
	// it was rendering lowercase; passing "Forms" capitalised keeps it
	// matching the "Properties" / "Documents" breadcrumb tier.
	const breadcrumbs: any[] = [
		{ title: "Properties", link: "/properties" },
	];
	if (propertyName) {
		breadcrumbs.push({
			title: propertyName,
			link: propertyId ? `/properties/${propertyId}` : undefined,
		});
	}
	breadcrumbs.push({ title: "Forms" });
	breadcrumbs.push({ title: form.name || form.code });

	const headingTitle = form.name || form.code;
	const headingSubtitle = propertyName || form.code;

	const queryStringParameters = { form_id: id };

	return (
		<>
			<Breadcrumbs items={breadcrumbs} />
			{/* F1: Snapshot badge — rendered only when parent_form_id is non-null.
			    Contract: api-contract.md §2.4; task: react-frontend-tasks.md F1. */}
			{isSnapshot && (
				<div
					style={{
						display: "inline-flex",
						alignItems: "center",
						background: "#FEF3C7",
						color: "#92400E",
						border: "1px solid #FCD34D",
						borderRadius: 999,
						padding: "2px 10px",
						fontSize: 12,
						fontWeight: 600,
						marginBottom: 8,
					}}
					data-testid="snapshot-badge"
				>
					Snapshot of {snapshotOfCode}
				</div>
			)}
			<Header
				resource={form}
				title={headingTitle}
				subtitle={headingSubtitle}
				toolbar={[
					downloadable && { label: "download", onClick: onDownload },
					canEdit && { label: "edit", onClick: () => setEditOpen(true) },
					canReplace && { label: "replace", onClick: () => setReplaceOpen(true) },
					canEdit && { label: "trash", onClick: () => setDeleteOpen(true) },
				]}
			/>
			{canReplace && (
				<ReplaceModal
					isOpen={replaceOpen}
					loading={replacing}
					onRequestClose={() => setReplaceOpen(false)}
					// Opens the file picker and leaves the modal STANDING. The spinner
					// starts when a file is actually chosen, not here: the browser fires
					// no event when someone dismisses a file dialog, so spinning from
					// this click would leave it spinning forever on a cancel.
					onConfirm={() => replaceInputRef.current?.click()}
				/>
			)}
			{canReplace && (
				<input
					ref={replaceInputRef}
					type="file"
					accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
					style={{ display: "none" }}
					onChange={onReplaceFile}
					disabled={replacing}
				/>
			)}
			<Workspace
				id={form.id}
				resource="forms"
				className="show"
				tabs={[
					<View key="form-preview" name="preview" className={["preview", "form-preview"]}>
						{downloadUrl ? (
							// Forms are always .docx — the replace picker only accepts .docx.
							<Office url={downloadUrl} kind="word" name={form.name || form.code} />
						) : (
							<div className="alert alert-warning">
								This form has no uploaded .docx yet — nothing to preview.
							</div>
						)}
					</View>,
					<Insights key="form-insights" form={form} name="dashboard" />,
					<ActivityTab
						key="form-activity"
						name="activity"
						queryStringParameters={queryStringParameters}
						context="property"
					/>,
				]}
			/>
			{canEdit && (
				<EditModal
					form={form}
					isOpen={editOpen}
					onRequestClose={() => setEditOpen(false)}
					onSaved={() => fetchForm()}
				/>
			)}
			{canEdit && (
				<DeleteModal
					isOpen={deleteOpen}
					onRequestClose={() => setDeleteOpen(false)}
					formId={form.id}
					onDeleteSuccess={() => {
						// DeleteModal deletes via the raw Datasource, which does not
						// touch the redux store. Dispatch DELETE_FORM_SUCCESS so the
						// form is removed from `state.forms` immediately — otherwise the
						// destination property's Forms tab (which reads state.forms and
						// only ever unions in getForms results) keeps showing the
						// deleted form until a hard refresh.
						dispatch({ type: "DELETE_FORM_SUCCESS", resource: { id: form.id } });
						// Land on the property's Forms tab, not its first tab — that is
						// where the deleted form was listed. Shared/Tabs picks the tab
						// from the hash on mount, and "forms" is PropertyFormsTab's name.
						history.push(propertyId ? `/properties/${propertyId}#forms` : "/properties");
					}}
				/>
			)}
		</>
	);
};

export default memo(FormShow);
